/* 
 This module provides all chat-related API interactions for the application.
 It handles:
  - Chat session management (creation, updates, deletion)
  - Message management
  - Metadata handling
  - API communication with authentication 
 All functions include error handling and proper typing for responses.
 The service uses axios for HTTP requests and maintains consistent
 authentication header handling.
 */

import axiosInstance, { extractErrorMessage } from './axiosConfig';
import { Message, Session } from '../types/chat';
import { Sentry } from '../config/sentry';
import { emitSessionUpdated } from '../utils/chatEvents';

// Constants from axiosConfig to ensure consistency
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api/v1`;
const API_BASE_BACKUP_URL = `https://backup.${BACKEND_URL.split("/").pop()}/api/v1`;

/*
 Create a new chat session
 Initializes a new chat session on the server

  @async
  @param {number} [agentId] - Optional agent ID to associate with the session
  @param {string | null} [projectId] - Optional project ID to associate with the session
  @param {number} [workspaceId] - Optional workspace ID to attribute token usage to (CRITICAL for proper token accounting)
  @returns {Promise<{success: boolean, data: Session}>} New session data
  @throws {Error} If session creation fails

  @example
  ```typescript
  try {
    const newSession = await createChatSession(123, 'project-uuid', 456); // With agent, project, and workspace
    // OR
    const newSession = await createChatSession(123, null, 456); // With agent and workspace
    // OR
    const newSession = await createChatSession(undefined, 'project-uuid', 456); // With project and workspace
    console.log('Created session:', newSession.data);
  } catch (error) {
    console.error('Failed to create session:', error);
  }
  ```

  IMPORTANT: workspaceId is CRITICAL for proper token accounting in multi-tenant system.
  Tokens consumed in this session will be attributed to the specified workspace.
  Always pass the user's active workspace ID to ensure tokens are counted correctly.
 */
export const createChatSession = async (
  agentId?: number,
  projectId?: string | null,
  workspaceId?: number
): Promise<{
  success: boolean;
  data: Session;
}> => {
  try {
    const params: { agent_id?: number; project_id?: string; workspace_id?: number } = {};

    if (agentId) {
      params.agent_id = agentId;
    }

    if (projectId) {
      params.project_id = projectId;
    }

    // ✅ CRITICAL: Always pass workspace_id for proper token accounting
    if (workspaceId) {
      params.workspace_id = workspaceId;
    }

    const response = await axiosInstance.post(
      '/chat/create-session',
      null,
      {
        params: Object.keys(params).length > 0 ? params : undefined
      }
    );

    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao criar sessão de chat'
      )
    );
  }
};

/*
  Get all chat sessions for the current user
  Retrieves a list of all chat sessions associated with the authenticated user
  
  @async
  @param {number} [limit=20] - Maximum number of sessions to return
  @param {number} [offset=0] - Number of sessions to skip
  @returns {Promise<{success: boolean, data: Session[], metadata: PaginationMetadata}>} List of user's sessions with pagination metadata
  @throws {Error} If sessions cannot be fetched
  
  @example
  ```typescript
  try {
    const sessions = await getUserSessions(50, 0);
    console.log('User sessions:', sessions.data);
    console.log('Has more:', sessions.metadata.has_more);
  } catch (error) {
    console.error('Failed to fetch sessions:', error);
  }
  ```
 */
export const getUserSessions = async (
  limit: number = 20,
  offset: number = 0,
  includeProjects: boolean = false
): Promise<{
  success: boolean;
  data: Session[];
  metadata: {
    total: number;
    page: number;
    pages: number;
    has_more: boolean;
  };
}> => {
  try {
    const response = await axiosInstance.get('/chat/sessions', {
      params: {
        limit,
        offset,
        exclude_projects: !includeProjects
      }
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar sessões'
      )
    );
  }
};

/*
  Get details of a specific chat session
  Retrieves complete information for a single chat session
  
  @async
  @param {number} sessionId - ID of the session to fetch
  @returns {Promise<{success: boolean, data: Session}>} Session details
  @throws {Error} If session cannot be fetched
  
  @example
  ```typescript
  try {
    const session = await getSession(123);
    console.log('Session details:', session.data);
  } catch (error) {
    console.error('Failed to fetch session:', error);
  }
  ```
 */
export const getSession = async (sessionId: number): Promise<{
  success: boolean;
  data: Session;
}> => {
  try {
    const response = await axiosInstance.get(`/chat/sessions/${sessionId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar sessão'
      )
    );
  }
};

/*
  Get all messages for a specific chat session
  Retrieves the message history for a given session ID
  
  @async
  @param {number} sessionId - ID of the session to fetch messages for
  @param {number} [limit=50] - Maximum number of messages to return
  @param {string} [before] - Return messages before this timestamp (ISO string)
  @returns {Promise<{success: boolean, data: Message[]}>} List of session messages
  @throws {Error} If messages cannot be fetched
  
  @example
  ```typescript
  try {
    const messages = await getMessages(123, 50);
    console.log('Session messages:', messages.data);
  } catch (error) {
    console.error('Failed to fetch messages:', error);
  }
  ```
 */
export const getMessages = async (
  sessionId: number, 
  limit: number = 50, 
  before?: string
): Promise<{
  success: boolean;
  data: Message[];
}> => {
  try {
    const params: Record<string, unknown> = { limit };
    if (before) {
      params.before = before;
    }
    
    const response = await axiosInstance.get(`/chat/sessions/${sessionId}/messages`, {
      params
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao buscar mensagens'
      )
    );
  }
};

/*
  Update the title of a chat session
  Modifies the display title for a specific chat session
  
  @async
  @param {number} sessionId - ID of the session to update
  @param {string} title - New title for the session
  @returns {Promise<{success: boolean, data: Session}>} Updated session data
  @throws {Error} If title update fails
  
  @example
  ```typescript
  try {
    const updatedSession = await updateSessionTitle(123, "New Title");
    console.log('Updated session:', updatedSession.data);
  } catch (error) {
    console.error('Failed to update title:', error);
  }
  ```
 */
export const updateSessionTitle = async (
  sessionId: number,
  title: string
): Promise<{
  success: boolean;
  data: Session;
}> => {
  try {
    const response = await axiosInstance.patch(
      `/chat/sessions/${sessionId}`,
      null,
      { params: { chat_title: title } }
    );

    // Emit event to notify sidebar about title update
    if (response.data.success) {
      emitSessionUpdated(sessionId);
    }

    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao atualizar título da sessão'
      )
    );
  }
};

/*
  Delete a chat session
  Removes a chat session and its associated messages (soft delete)
  
  @async
  @param {number} sessionId - ID of the session to delete
  @returns {Promise<{success: boolean, message: string}>} Deletion confirmation
  @throws {Error} If deletion fails
  
  @example
  ```typescript
  try {
    const result = await deleteSession(123);
    console.log('Deletion result:', result.message);
  } catch (error) {
    console.error('Failed to delete session:', error);
  }
  ```
 */
export const deleteSession = async (sessionId: number): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await axiosInstance.delete(`/chat/sessions/${sessionId}`);
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao deletar sessão'
      )
    );
  }
};

/*
  Submit feedback for a chat message
  Allows users to provide feedback (e.g., helpful/not helpful) for AI responses
  
  @async
  @param {number} sessionId - ID of the session containing the message
  @param {number} messageId - ID of the message to provide feedback for
  @param {string} feedback - Feedback value to submit
  @returns {Promise<{success: boolean, message: string}>} Feedback submission confirmation
  @throws {Error} If feedback submission fails
  
  @example
  ```typescript
  try {
    const result = await submitFeedback(123, 456, "helpful");
    console.log('Feedback result:', result.message);
  } catch (error) {
    console.error('Failed to submit feedback:', error);
  }
  ```
 */
export const submitFeedback = async (
  sessionId: number,
  messageId: number,
  feedback: string
): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await axiosInstance.post(
      `/chat/sessions/${sessionId}/feedback`,
      null,
      { params: { message_id: messageId, feedback } }
    );
    return response.data;
  } catch (err: unknown) {
    throw new Error(
      extractErrorMessage(
        err instanceof Error ? err : new Error(String(err)),
        'Falha ao enviar feedback'
      )
    );
  }
};

/*
  Send a message to an existing chat session
  Sends a message and handles streaming response from the server

  @async
  @param {number} sessionId - ID of the chat session
  @param {string} message - Message to send
  @param {File[]} [files] - Optional array of files to attach
  @returns {Promise<Response>} Fetch Response object for streaming
  @throws {Error} If message cannot be sent

  @example
  ```typescript
  try {
    const response = await sendMessage(123, "Hello", [file1, file2]);
    const reader = response.body?.getReader();
    // Handle streaming...
  } catch (error) {
    console.error('Failed to send message:', error);
  }
  ```
 */
export const sendMessage = async (sessionId: number, message: string, files?: File[]): Promise<Response> => {
  const token = localStorage.getItem('token');

  // Create FormData for multipart/form-data request
  const formData = new FormData();
  formData.append('question', message);

  // Add files if provided
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Accept': 'text/event-stream'
    // Note: Don't set Content-Type header - browser will set it automatically with boundary
  };
  const body = formData;

  // First try with primary URL
  try {
    const primaryEndpoint = `${API_BASE_URL}/chat/sessions/${sessionId}/messages`;
    const response = await fetch(primaryEndpoint, {
      method: 'POST',
      headers,
      body
    });

    if (response.ok) {
      return response;
    }

    // Try to extract error details for non-OK responses
    const errorData = await tryParseErrorResponse(response);

    // Handle 429 Token Limit Exceeded specifically
    if (response.status === 429 && errorData && typeof errorData === 'object' && 'detail' in errorData) {
      const detail = errorData.detail;

      // If detail is an object with error field (our custom format)
      if (typeof detail === 'object' && detail !== null && 'error' in detail) {
        const errorInfo = detail as {
          error?: string;
          message?: string;
          tokens_used?: number;
          token_limit?: number;
          estimated_usage?: number;
          tokens_remaining?: number;
          upgrade_url?: string;
        };

        // Throw with special format that can be caught and displayed properly
        const error = new Error(errorInfo.message || 'Limite de tokens excedido');
        (error as Error & { tokenLimitError?: unknown }).tokenLimitError = errorInfo;
        throw error;
      }
    }

    const detail = (errorData && typeof errorData === 'object' && 'detail' in errorData) ? (errorData as { detail?: string }).detail : undefined;
    throw new Error(detail || `${response.status}: ${response.statusText}`);
  } catch (primaryError: unknown) {
    // Don't report primary error to Sentry, just try backup URL
    
    // Try backup URL
    try {
      const backupEndpoint = `${API_BASE_BACKUP_URL}/chat/sessions/${sessionId}/messages`;
      const response = await fetch(backupEndpoint, {
        method: 'POST',
        headers,
        body
      });

      if (response.ok) {
        return response;
      }

      // Handle non-OK response from backup
      const errorData = await tryParseErrorResponse(response);

      // Handle 429 Token Limit Exceeded specifically
      if (response.status === 429 && errorData && typeof errorData === 'object' && 'detail' in errorData) {
        const detail = errorData.detail;

        // If detail is an object with error field (our custom format)
        if (typeof detail === 'object' && detail !== null && 'error' in detail) {
          const errorInfo = detail as {
            error?: string;
            message?: string;
            tokens_used?: number;
            token_limit?: number;
            estimated_usage?: number;
            tokens_remaining?: number;
            upgrade_url?: string;
          };

          // Throw with special format that can be caught and displayed properly
          const error = new Error(errorInfo.message || 'Limite de tokens excedido');
          (error as Error & { tokenLimitError?: unknown }).tokenLimitError = errorInfo;
          throw error;
        }
      }

      const detail = (errorData && typeof errorData === 'object' && 'detail' in errorData) ? (errorData as { detail?: string }).detail : undefined;
      throw new Error(detail || `${response.status}: ${response.statusText}`);
    } catch (backupError: unknown) {
      // Only log to Sentry after both primary and backup URLs have failed
      Sentry.captureException(backupError, {
        tags: {
          errorType: 'frontend_network',
          component: 'chat_service',
          endpoint: `/chat/sessions/${sessionId}/messages`
        },
        extra: {
          primaryUrl: `${API_BASE_URL}/chat/sessions/${sessionId}/messages`,
          backupUrl: `${API_BASE_BACKUP_URL}/chat/sessions/${sessionId}/messages`,
          method: 'POST',
          timestamp: new Date().toISOString(),
          primaryError: primaryError instanceof Error ? primaryError.message : String(primaryError),
          networkInfo: {
            online: navigator.onLine,
            userAgent: navigator.userAgent,
            connectionType: typeof (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType === 'string'
              ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
              : null,
          }
        }
      });

      // Create user-friendly error message
      const userMessage = 'Não foi possível conectar ao servidor. Por favor, tente novamente daqui a alguns instantes.';
      throw new Error(userMessage);
    }
  }
};

/**
 * Helper function to try parsing error response
 * @param response - Fetch Response object
 * @returns Parsed error data or null if parsing fails
 */
const tryParseErrorResponse = async (response: Response): Promise<{ detail?: string } | null> => {
  try {
    const clonedResponse = response.clone();
    return await clonedResponse.json();
  } catch {
    return null;
  }
};